import type { OrganisationRole } from "./enums.js";
import type { MailboxId, OrganisationId, SessionId, UserId } from "./ids.js";

export interface SessionPrincipal {
	sessionId: SessionId;
	userId: UserId;
	email: string;
	fullName: string;
	/** Organisations the user belongs to, with the role held in each. */
	memberships: Array<{
		organisationId: OrganisationId;
		organisationName: string;
		organisationSlug: string;
		role: OrganisationRole;
		logo?: string | null;
		viewAllHubTasks?: boolean;
	}>;
	/** The organisation the current request is scoped to. */
	activeOrganisationId: OrganisationId | null;
	twoFactorVerified: boolean;
	/** When the user last proved their password, for sensitive-action gating. */
	reauthenticatedAt: Date | null;
}

export interface OperatorPrincipal {
	userId: UserId;
	email: string;
	role: "operator" | "operator_admin";
}

export const MAILBOX_GRANT_ACCESS = ["owner", "full", "read_only"] as const;
export type MailboxGrantAccess = (typeof MAILBOX_GRANT_ACCESS)[number];

/**
 * Grants a Bafe user the right to open a specific mailbox in webmail.
 * Membership in an organisation is not by itself mailbox access.
 */
export interface MailboxGrant {
	id: string;
	mailboxId: MailboxId;
	userId: UserId;
	organisationId: OrganisationId;
	access: MailboxGrantAccess;
	showInSidebar: boolean;
	createdAt: Date;
}

export type Permission =
	| "org:read"
	| "org:manage"
	| "org:transfer"
	| "billing:read"
	| "billing:manage"
	| "domain:read"
	| "domain:manage"
	| "mailbox:read"
	| "mailbox:manage"
	| "alias:read"
	| "alias:manage"
	| "migration:read"
	| "migration:manage"
	| "support:read"
	| "support:manage"
	| "audit:read"
	| "engage:read"
	| "engage:admin"
	| "engage:assign"
	| "engage:reply"
	| "hub:read"
	| "hub:manage";

export const ROLE_PERMISSIONS: Record<OrganisationRole, readonly Permission[]> = {
	owner: [
		"org:read",
		"org:manage",
		"hub:read",
		"hub:manage",
		"org:transfer",
		"billing:read",
		"billing:manage",
		"domain:read",
		"domain:manage",
		"mailbox:read",
		"mailbox:manage",
		"alias:read",
		"alias:manage",
		"migration:read",
		"migration:manage",
		"support:read",
		"support:manage",
		"audit:read",
		"engage:read",
		"engage:admin",
		"engage:assign",
		"engage:reply",
	],
	admin: [
		"org:read",
		"hub:read",
		"hub:manage",
		"billing:read",
		"domain:read",
		"domain:manage",
		"mailbox:read",
		"mailbox:manage",
		"alias:read",
		"alias:manage",
		"migration:read",
		"migration:manage",
		"support:read",
		"support:manage",
		"audit:read",
		"engage:read",
		"engage:admin",
		"engage:assign",
		"engage:reply",
	],
	/** Key Mail via mailbox grants, plus assigned Hub tasks. No workspace management APIs. */
	user: ["hub:read"],
	billing: ["org:read", "hub:read", "billing:read", "billing:manage", "support:read", "support:manage"],
	support_viewer: [
		"org:read",
		"hub:read",
		"domain:read",
		"mailbox:read",
		"alias:read",
		"migration:read",
		"support:read",
		"engage:read",
		"engage:reply",
	],
};

export function roleHasPermission(role: OrganisationRole, permission: Permission): boolean {
	return ROLE_PERMISSIONS[role].includes(permission);
}

/** Owners and admins always see every workspace task. Others need an explicit grant. */
export function canViewAllHubTasks(role: OrganisationRole, granted: boolean): boolean {
	return role === "owner" || role === "admin" || granted;
}
